import { AttachmentError } from 'shared/components/email-attachments/attachment-error';
import { Attachment } from './attachment.model';
import { AttachmentUploadResponseModel } from './attachment-upload-response.model';

export class Attachments {
  private _storage;
  public bcAnalytics;
  public addEditorMessage;
  public translateParams;
  public config;
  public fileUploadService;
  public xlcEmails;
  public settings;
  public editorMessages;

  constructor(BcSettingsService, BcAnalytics, editorMessages, FileUploadService, BcConfig, xlcEmails, attachmentStorage) {
    this.bcAnalytics = BcAnalytics;
    this.editorMessages = editorMessages;
    this.config = BcConfig;
    this.fileUploadService = FileUploadService;
    this.xlcEmails = xlcEmails;

    BcSettingsService.getSettings().then(settings => {
      this.settings = settings;
    });

    if (!attachmentStorage) {
      throw new Error('Please specify an attachment storage when creating a new Attachments instance!');
    }
    this._storage = attachmentStorage;
  }

  get files() {
    return this._storage.files;
  }

  addAttachment(file) {
    this._storage.addFile(file);
  }

  deleteAttachment(file) {
    if (file.canDelete) {
      this._storage.deleteFile(file);
    }
  }

  isEmpty() {
    return this._storage.isEmpty();
  }

  dropFileHandler(event): any {
    const eventAttachment = event.attachment;
    const droppedFile = eventAttachment.file;

    // No attachment file
    if (typeof droppedFile === 'undefined' || droppedFile === null) {
      return false;
    }

    return this.uploadIfAllowedFileType(droppedFile);
  }

  uploadIfAllowedFileType(droppedFile) {
    const context = this;

    context.editorMessages.removeEditorMessage('attachmentOverSizeLimitMessage');
    context.editorMessages.removeEditorMessage('attachmentFileTypeNotAllowed');

    // In case of unallowed file types.
    if (context.isForbiddenFileType(droppedFile, this.settings.email.attachment.forbiddenExtensions)) {
      context.editorMessages.addEditorMessage('attachmentFileTypeNotAllowed', 'bcEmailBox.attachmentFileTypeNotAllowed');
    } else {
      const fileSize = parseInt((droppedFile.size / 1024).toString(), 10);
      if (fileSize > context.config.current.fileUploadSizeLimit) {
        context.editorMessages.addEditorMessage(
          'attachmentOverSizeLimitMessage',
          'bcEmailBox.attachmentOverSizeLimitMessage',
          { sizeLimit: context.config.current.fileUploadSizeLimit / 1024 });
      } else {
        droppedFile.canDelete = true;
        droppedFile.fileName = droppedFile.name;
        droppedFile.contentType = droppedFile.type;
        droppedFile.downloadUrl = URL.createObjectURL(droppedFile);
        context.xlcEmails.decorateEmailAttachment(droppedFile);
        context.addAttachment(droppedFile);
      }
    }
  }

  isForbiddenFileType(file, forbiddenExtensionsList) {
    // If no file / filename it is forbidden
    if (!file || !file.name || file.name === '') {
      return true;
    }
    // If matches any extension, it is forbidden
    for (let i = 0; i < forbiddenExtensionsList.length; i++) {
      const forbiddenExtension = forbiddenExtensionsList[i];
      if (file.name.toLowerCase().endsWith(forbiddenExtension.toLowerCase())) {
        return true;
      }
    }
    return false;
  }

  async uploadAttachments(emailThreadId): Promise<AttachmentUploadResponseModel> {
    let response: AttachmentUploadResponseModel;
    const attachmentsNotUpload: Attachment[] = [];
    const filesNotForUpload = this._storage.files.filter(file => !(file instanceof File));
    filesNotForUpload.forEach((file: Attachment) => {
      attachmentsNotUpload.push(file);
    });
    const filesForUpload = this._storage.files.filter(file => file instanceof File);
    if (filesForUpload.length > 0) {
      response = await this.fileUploadService.upload(filesForUpload, emailThreadId);
    } else {
      response = { data : [] };
    }
    response.data.unshift(...attachmentsNotUpload);
    return response;
  }

  uploadFiles(file) {
    this.dropFileHandler({ attachment: { file } });
    this.bcAnalytics.eventTrack('EmailFileAttached', { size: file && file.size });
  }

  validateAttachmentsAvailability() {
    let attachmentsValid = true;
    this._storage.files.forEach((attachment) => {
      if (!(attachment instanceof File) && !(attachment.attachmentId)) {
        this._storage.clear();
        attachmentsValid = false;
      }
    });
    return attachmentsValid ? Promise.resolve({}) : Promise.reject(new AttachmentError());
  }
}
